import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { endpoints as countryEndpoints } from './country/endpoints';
import { endpoints as informationEndpoints } from './exhibitor/information/endpoints';
import { endpoints as participationEndpoints } from './exhibitor/participation/endpoints';
import { endpoints as artObjectEndpoints } from './exhibitor/artObject/endpoints';
import { endpoints as bookmarkEndpoints } from './bookmarks/endpoints';
import { endpoints as vetterVettingHistoryEndpoints } from './vetter/vettingHistory/endpoints';
import { endpoints as tefafArtObjectEndpoints } from './tefaf/artObject/endpoints';
import { endpoints as tefafFairEndpoints } from './tefaf/fair/endpoints';
import { endpoints as tefafVettingExpensesEndpoints } from './tefaf/vettingExpenses/endpoints';
import { endpoints as tefafVettingExpensesCurrencyExchangeRateEndpoints } from './tefaf/vettingExpensesCurrencyExchangeRate/endpoints';
import { endpoints as pressPortalAccessEndpoints } from './tefaf/pressPortalAccess/endpoints';
import { endpoints as milestoneEndpoints } from './exhibitor/milestones/endpoints';
import { endpoints as tefafMilestoneEndpoints } from './tefaf/milestones/endpoints';
import { endpoints as collectorFavouritesEndpoints } from './collector/favourites/endpoints';
import { endpoints as collectorUserEndpoints } from './collector/user/endpoints';
import { list as countryTags } from './country/tags';
import { tags as informationTags } from './exhibitor/information/tags';
import { tags as participationTags } from './exhibitor/participation/tags';
import { list as artObjectTags, categoryValues as artObjectCategoryValuesTags } from './exhibitor/artObject/tags';
import { list as bookmarkTags } from './bookmarks/tags';
import {
  list as vetterVettingHistoryTags,
  vettingParticipation as vetterVettingParticipationTags,
  vettingCommitteeList as vetterVettingCommitteeListTags,
} from './vetter/vettingHistory/tags';
import {
  list as tefafArtObjectTags,
  categoryValues as tefafArtObjectCategoryValuesTags,
  comments as tefafArtObjectCommentsTags,
} from './tefaf/artObject/tags';
import { listContracts as tefafListContractTags } from './tefaf/fair/tags';
import { list as tefafVettingExpensesTags } from './tefaf/vettingExpenses/tags';
import { list as vettingExpensesCurrencyExchangeRateTags } from './tefaf/vettingExpensesCurrencyExchangeRate/tags';
import { list as pressPortalAccessTags } from './tefaf/pressPortalAccess/tags';
import { tags as milestoneTags } from './exhibitor/milestones/tags';
import { list as tefafMilestoneTags } from './tefaf/milestones/tags';
import { list as collectorFavouritesTags } from './collector/favourites/tags';

export const tefafApi = {};
Object.assign(tefafApi, createApi({
  reducerPath: 'tefafApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${window.location.protocol}//${window.location.hostname}`,
    prepareHeaders: (headers) => {
      headers.set('accept', 'application/json');
      return headers;
    },
  }),
  tagTypes: [
    ...countryTags,
    ...informationTags,
    ...participationTags,
    ...artObjectTags,
    ...artObjectCategoryValuesTags,
    ...bookmarkTags,
    ...vetterVettingHistoryTags,
    ...vetterVettingParticipationTags,
    ...vetterVettingCommitteeListTags,
    ...tefafArtObjectTags,
    ...tefafArtObjectCategoryValuesTags,
    ...tefafArtObjectCommentsTags,
    ...tefafListContractTags,
    ...tefafVettingExpensesTags,
    ...vettingExpensesCurrencyExchangeRateTags,
    ...pressPortalAccessTags,
    ...milestoneTags,
    ...tefafMilestoneTags,
    ...collectorFavouritesTags,
  ],
  endpoints: (builder) => ({
    ...countryEndpoints(builder),
    ...informationEndpoints(builder),
    ...participationEndpoints(builder),
    ...artObjectEndpoints(builder),
    ...bookmarkEndpoints(builder, tefafApi),
    ...vetterVettingHistoryEndpoints(builder, tefafApi),
    ...tefafArtObjectEndpoints(builder),
    ...tefafFairEndpoints(builder),
    ...tefafVettingExpensesEndpoints(builder),
    ...tefafVettingExpensesCurrencyExchangeRateEndpoints(builder),
    ...pressPortalAccessEndpoints(builder),
    ...milestoneEndpoints(builder),
    ...tefafMilestoneEndpoints(builder),
    ...collectorFavouritesEndpoints(builder, tefafApi),
    ...collectorUserEndpoints(builder),
  }),
}));

export const {
  util,
  useGetCountriesQuery,
  useGetInformationQuery,
  useUpdateInformationMutation,
  useCreateAddressMutation,
  useRemoveAddressMutation,
  useCreateContactMutation,
  useRemoveContactMutation,
  useSaveUserMutation,
  useRemoveUserMutation,
  useGetParticipationQuery,
  useEditContactsMutation,
  useGetArtObjectCategoryValuesQuery,
  useGetArtObjectsQuery,
  useCreateArtObjectMutation,
  useRequestEditArtObjectMutation,
  useDeleteDraftArtObjectMutation,
  useGetBookmarkedElementIdsQuery,
  useAddBookmarkMutation,
  useRemoveBookmarkMutation,
  useGetVetterVettingParticipationQuery,
  useGetVetterArtObjectsQuery,
  useLazyGetVetterArtObjectsQuery,
  useGetVetterArtObjectQuery,
  useLazyGetVetterArtObjectQuery,
  useCreateArtObjectVettingHistoryMutation,
  useUpdateArtObjectVettingHistoryMutation,
  useDisableArtObjectVettingHistoryMutation,
  useDeleteVettingHistoryMutation,
  useGetCommitteeQuery,
  useGetArtObjectCategoryValuesByFairQuery,
  useGetArtObjectsByFairQuery,
  useEditArtObjectUsageMutation,
  useGetArtObjectTefafCommentsQuery,
  useCreateArtObjectTefafCommentMutation,
  useCreateArtObjectTefafReplyMutation,
  useGetFairContractsQuery,
  useLazyGetFairContractsQuery,
  useCreateFairContractMutation,
  useGetVettingExpensesByFairQuery,
  useGetVettingExpenseEntryQuery,
  useAcceptVettingExpenseEntryMutation,
  useRejectVettingExpenseEntryMutation,
  useGetVettingExpensesCurrencyExchangeRateQuery,
  useCreateVettingExpensesCurrencyExchangeRateMutation,
  useGetPortalAccessSubmissionsQuery,
  useAcceptPortalAccessSubmissionMutation,
  useRejectPortalAccessSubmissionMutation,
  useGetMilestonesQuery,
  useGetExhibitorMilestonesByFairQuery,
  useGetCollectorFavouritesQuery,
  useToggleCollectorFavouriteMutation,
  useLoginCollectorUserMutation,
  useSendPasswordResetEmailCollectorUserMutation,
  useSetPasswordCollectorUserMutation,
} = tefafApi;
